import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, BookOpen, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Student-facing library page — lists all H1 chapters the current user has
 * access to. Triggered for any logged-in user; access enforced server-side.
 */
export const CourseLibraryPage = () => {
  const { lang = 'fr' } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/course-content/my/library`, { headers: authHeaders() });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Accès restreint.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F0]">
      <Loader2 className="animate-spin text-[#1F2A4A]" />
    </div>
  );

  if (error) return (
    <div className="min-h-[70vh] flex items-center justify-center bg-[#FAF7F0] px-6">
      <div className="text-center max-w-md">
        <Lock className="mx-auto text-[#C9B896] mb-4" size={40} />
        <h1 className="font-serif text-3xl text-[#1F2A4A] font-light mb-3">Accès restreint</h1>
        <p className="text-[#4A5568]">{error}</p>
        <Link to={`/${lang}/formations`} className="mt-6 inline-block px-6 py-2.5 bg-[#1F2A4A] text-white rounded-full text-sm">
          Découvrir nos formations
        </Link>
      </div>
    </div>
  );

  const chapters = data?.chapters || [];

  return (
    <div className="min-h-screen bg-[#FAF7F0] py-16" data-testid="course-library">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <p className="text-xs font-mono tracking-[0.3em] text-[#C9B896] mb-3">BIBLIOTHÈQUE</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#1F2A4A] font-light mb-3">
          Tes cours, <span className="italic">à portée de main.</span>
        </h1>
        <p className="text-[#4A5568] mb-12 font-light max-w-2xl">
          Tous tes supports de cours en ligne. Lecture sécurisée, sans téléchargement, sans copie.
        </p>

        {chapters.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-[#C9B896]/20">
            <BookOpen className="mx-auto text-[#C9B896] mb-4" size={36} />
            <p className="text-[#4A5568]">Aucun chapitre disponible pour le moment.</p>
            <p className="text-sm text-[#4A5568]/70 mt-2">Tes accès s&apos;activent dès la validation d&apos;une inscription.</p>
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 gap-5">
            {chapters.map((c, idx) => (
              <li key={c.id}>
                <Link
                  to={`/${lang}/cours-en-ligne/${c.slug}`}
                  data-testid={`library-card-${c.slug}`}
                  className="group block bg-white border border-[#C9B896]/30 hover:border-[#1F2A4A] rounded-2xl p-6 transition-all hover:shadow-xl"
                >
                  <p className="text-[10px] font-mono tracking-wider text-[#C9B896] mb-2">CHAPITRE {String(idx + 1).padStart(2, '0')}</p>
                  <h2 className="font-serif text-xl text-[#1F2A4A] font-light group-hover:italic transition-all leading-snug">
                    {c.title}
                  </h2>
                  <p className="mt-4 text-xs text-[#C9B896] group-hover:text-[#1F2A4A] uppercase tracking-wider">
                    Lire le chapitre →
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CourseLibraryPage;
